import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * The set of child ASINs that currently have a cached product image.
 *
 * A page can show hundreds of thumbnails at once. Without this, every one whose
 * ASIN has no image would fire an authenticated GET /api/catalog/images/:asin
 * that 404s — hundreds of requests that do nothing but compete with the SPA's
 * real API calls. Instead the whole app shares ONE small request for the set of
 * ASINs that DO have an image (TanStack Query dedupes it across every mounted
 * ProductThumb), and a thumbnail renders its placeholder directly — no request —
 * when its ASIN is not in the set.
 *
 * The set is small (one seller's ASINs) and changes only when a catalog fetch
 * runs, so it is cached for a long while; a fetch on the connection card can
 * invalidate ['catalog', 'image-asins'] to refresh it once the run has stored.
 *
 * While the set is still loading (or if the request failed) `has` returns false,
 * so a thumbnail renders its placeholder with no per-row request until the set
 * arrives, then flips to the image on the next render. That is deliberate: it is
 * what keeps a fresh page of hundreds of rows from firing hundreds of 404s before
 * the set is known.
 */
export function useImageAsins(): { has: (asin: string) => boolean } {
  const query = useQuery({
    queryKey: ['catalog', 'image-asins'],
    queryFn: () => api.get<{ asins: string[] }>('/api/catalog/image-asins'),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const set = query.data ? new Set(query.data.asins) : null;
  return { has: (asin: string) => (set ? set.has(asin) : false) };
}
